import { useNavigate } from "react-router-dom";

import LaunchIcon from "@mui/icons-material/Launch";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { misc, neutral, primary } from "app/themes/colors";
import useSunoDialer from "components/suno-dialer/use-suno-dialer.hook";
import { BAS, CRM_SEGMENTS, PHONE_TYPES } from "constants/patients";

import PhoneContact from "./phone-contact.component";

export default function SelectedPatient() {
  const theme = useTheme();
  const navigate = useNavigate();

  const { outgoingCall } = useSunoDialer();

  const patient = outgoingCall?.patient;
  if (!patient) return null;

  return (
    <Stack justifyContent="space-between" direction="row" sx={{ p: 0, pb: 0 }}>
      <Stack
        sx={{
          background: "hsla(0, 0%, 100%, 0.85)",
          color: primary[900],
          boxSizing: "border-box",
          p: 1.5,
          width: "300px",
          borderRadius: "16px",
          boxShadow: theme.shadows[3],
          "&:hover": {
            boxShadow: theme.shadows[0]
          }
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            alt="avatar"
            src={patient?.avatar || ""}
            sx={{
              width: "70px",
              height: "70px"
            }}
            slotProps={{ img: { loading: "lazy" } }}
          />

          <Stack width="100%" height="100%" sx={{ pl: 1 }}>
            <Link
              href={`/patients/${patient?.id}`}
              sx={{
                width: "fit-content",
                color: neutral[900],
                textDecorationColor: neutral[500],
                "&:hover": {
                  textDecoration: misc.purpleText
                }
              }}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                navigate(`/patients/${patient.id}`);
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "17px",
                  lineHeight: 1.3
                }}
              >
                {patient.fullName}
                <IconButton
                  size="small"
                  sx={{
                    ml: -0.5,
                    mt: -0.8,
                    transform: "scale(0.6)",
                    opacity: 0.5,
                    "&:hover": {
                      color: misc.purpleText,
                      opacity: 1
                    }
                  }}
                >
                  <LaunchIcon />
                </IconButton>
              </Typography>
            </Link>
            <Typography
              sx={{
                fontSize: "15px",
                lineHeight: 1.3
              }}
            >
              {patient?.crm_segment
                ? `${CRM_SEGMENTS.getFromCode(patient?.crm_segment)}`
                : null}
            </Typography>
            {Boolean(patient?.sex || patient?.age_years) && (
              <Typography
                sx={{
                  fontSize: "13px",
                  lineHeight: 1.3,
                  color: grey[800]
                }}
              >
                {[
                  BAS.getFromCode(patient?.sex),
                  patient?.age_years ? `${patient?.age_years} years old` : ""
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )}
            {Boolean(patient?.phone) && (
              <PhoneContact
                title={`Primary (${PHONE_TYPES.getFromCode(
                  patient?.phone_type
                )}):`}
                phoneNumber={patient?.phone ?? ""}
                notes={patient?.primary_contact_notes}
                patient={patient}
              />
            )}
            {Boolean(patient?.secondary_phone) && (
              <PhoneContact
                title="Secondary:"
                phoneNumber={patient?.secondary_phone ?? ""}
                notes={patient?.secondary_contact_notes}
                patient={patient}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
